import { PROPERTY_TYPES } from '@/lib/constants/mls';
import type {
  DateRangeFilterValue,
  PropertyAssetType,
  PublicPropertyFilterInput,
  PublicPropertyFilterQueryParams,
  PublicPropertyFilterState,
  RangeFilterValue,
  SanitizedFilterResult,
  UnitReadinessStatus,
} from '@/lib/types/publicProperty';

type QueryRecordValue = string | string[] | undefined;
type QueryRecord = Record<string, QueryRecordValue>;

const PROPERTY_TYPE_SET = new Set<PropertyAssetType>(
  Array.from(PROPERTY_TYPES) as PropertyAssetType[],
);

const READINESS_VALUES = [
  'available',
  'unavailable',
  'leased',
] as const satisfies readonly UnitReadinessStatus[];

const RANGE_LIMITS = {
  rentable: { min: 0, max: 1_000_000 },
  rent: { min: 0, max: 1_000_000 },
};

const DATE_RANGE_DEFAULT: DateRangeFilterValue = { start: null, end: null };
const NUMBER_RANGE_DEFAULT: RangeFilterValue = { min: null, max: null };

function cloneDateRange(): DateRangeFilterValue {
  return { ...DATE_RANGE_DEFAULT };
}

function cloneNumberRange(): RangeFilterValue {
  return { ...NUMBER_RANGE_DEFAULT };
}

function cloneDefaultState(): PublicPropertyFilterState {
  return {
    submarkets: [],
    assetTypes: [],
    rentableSquareFeet: cloneNumberRange(),
    rentPerSquareFoot: cloneNumberRange(),
    incentivesOnly: false,
    availability: cloneDateRange(),
    readinessStates: [],
    potentiallyDivisible: null,
  };
}

export function sanitizePublicPropertyFilters(
  filters?: PublicPropertyFilterInput | null,
): PublicPropertyFilterState {
  if (!filters) {
    return cloneDefaultState();
  }

  const next = cloneDefaultState();

  next.submarkets = dedupeStrings(filters.submarkets);
  next.assetTypes = dedupeAssetTypes(filters.assetTypes);
  next.rentableSquareFeet = sanitizeRange(filters.rentableSquareFeet, RANGE_LIMITS.rentable);
  next.rentPerSquareFoot = sanitizeRange(filters.rentPerSquareFoot, RANGE_LIMITS.rent);
  next.incentivesOnly = Boolean(filters.incentivesOnly);
  next.availability = sanitizeAvailability(filters.availability);
  next.readinessStates = sanitizeReadiness(filters.readinessStates);
  next.potentiallyDivisible = normalizeBoolean(filters.potentiallyDivisible);

  return next;
}

export function parsePublicPropertyFilterQuery(
  params: URLSearchParams | PublicPropertyFilterQueryParams | QueryRecord,
): SanitizedFilterResult {
  const record = normalizeQueryRecord(params);
  const assetValues = toStringArray(record.assetTypes ?? record.assetType);

  const filterInput: PublicPropertyFilterInput = {
    submarkets: toStringArray(record.submarkets),
    assetTypes: assetValues.filter(isAssetType),
    rentableSquareFeet: {
      min: coerceNumber(record.minSf),
      max: coerceNumber(record.maxSf),
    },
    rentPerSquareFoot: {
      min: coerceNumber(record.minRent),
      max: coerceNumber(record.maxRent),
    },
    incentivesOnly: coerceBoolean(record.incentives) ?? undefined,
    availability: {
      start: coerceDate(record.availabilityStart),
      end: coerceDate(record.availabilityEnd),
    },
    readinessStates: toStringArray(record.readiness).filter(isReadinessStatus),
    potentiallyDivisible: coerceBoolean(record.divisible) ?? null,
  };

  const state = sanitizePublicPropertyFilters(filterInput);
  const queryParams = buildPublicPropertyFilterQuery(state);

  return { state, queryParams };
}

export function buildPublicPropertyFilterQuery(state: PublicPropertyFilterState): URLSearchParams {
  const params = new URLSearchParams();

  const sortedSubmarkets = [...state.submarkets].sort((a, b) => a.localeCompare(b));
  sortedSubmarkets.forEach((value) => {
    params.append('submarkets', value);
  });

  const sortedAssetTypes = [...state.assetTypes].sort((a, b) => a.localeCompare(b));
  sortedAssetTypes.forEach((value) => {
    params.append('assetTypes', value);
  });

  appendRange(params, state.rentableSquareFeet, 'minSf', 'maxSf');
  appendRange(params, state.rentPerSquareFoot, 'minRent', 'maxRent');

  if (state.incentivesOnly) {
    params.set('incentives', 'true');
  }

  const { start, end } = state.availability;
  if (typeof start === 'string') {
    params.set('availabilityStart', start);
  }
  if (typeof end === 'string') {
    params.set('availabilityEnd', end);
  }

  const readiness = sortReadiness(state.readinessStates);
  readiness.forEach((value) => {
    params.append('readiness', value);
  });

  if (typeof state.potentiallyDivisible === 'boolean') {
    params.set('divisible', state.potentiallyDivisible ? 'true' : 'false');
  }

  return params;
}

function dedupeStrings(values?: string[] | null): string[] {
  if (!Array.isArray(values) || values.length === 0) {
    return [];
  }
  const seen = new Set<string>();
  values.forEach((value) => {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (normalized.length > 0) {
      seen.add(normalized);
    }
  });
  return Array.from(seen);
}

function dedupeAssetTypes(values?: PropertyAssetType[] | null): PropertyAssetType[] {
  if (!Array.isArray(values) || values.length === 0) {
    return [];
  }
  const seen = new Set<PropertyAssetType>();
  values.forEach((value) => {
    if (PROPERTY_TYPE_SET.has(value)) {
      seen.add(value);
    }
  });
  return Array.from(seen);
}

function sanitizeRange(
  range?: RangeFilterValue | null,
  limits?: { min?: number; max?: number },
): RangeFilterValue {
  const next = cloneNumberRange();
  const min = clampNumber(coerceNumber(range?.min), limits);
  const max = clampNumber(coerceNumber(range?.max), limits);

  if (min !== null) {
    next.min = min;
  }
  if (max !== null) {
    next.max = max;
  }

  if (next.min != null && next.max != null && next.min > next.max) {
    next.min = next.max;
  }

  return next;
}

function sanitizeAvailability(range?: DateRangeFilterValue | null): DateRangeFilterValue {
  const next = cloneDateRange();
  const start = coerceDate(range?.start);
  const end = coerceDate(range?.end);

  if (start !== null) {
    next.start = start;
  }
  if (end !== null) {
    next.end = end;
  }

  const startValue = next.start;
  const endValue = next.end;
  if (typeof startValue === 'string' && typeof endValue === 'string') {
    const startTime = Date.parse(startValue);
    const endTime = Date.parse(endValue);
    if (startTime > endTime) {
      next.start = endValue;
    }
  }

  return next;
}

function sanitizeReadiness(values?: UnitReadinessStatus[] | null): UnitReadinessStatus[] {
  if (!Array.isArray(values) || values.length === 0) {
    return [];
  }
  const seen = new Set<UnitReadinessStatus>();
  values.forEach((value) => {
    if (READINESS_VALUES.includes(value)) {
      seen.add(value);
    }
  });
  return sortReadiness(Array.from(seen));
}

function sortReadiness(values: UnitReadinessStatus[]): UnitReadinessStatus[] {
  return [...values].sort((a, b) => READINESS_VALUES.indexOf(a) - READINESS_VALUES.indexOf(b));
}

function normalizeBoolean(value: unknown): boolean | null {
  const parsed = coerceBoolean(value);
  if (typeof parsed === 'boolean') {
    return parsed;
  }
  return null;
}

function coerceBoolean(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'y', 'on'].includes(normalized)) {
      return true;
    }
    if (['0', 'false', 'no', 'n', 'off'].includes(normalized)) {
      return false;
    }
  }
  return null;
}

function coerceNumber(value: QueryRecordValue | number | null | undefined): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (Array.isArray(value)) {
    const [first] = value;
    return coerceNumber(first);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function clampNumber(value: number | null, limits?: { min?: number; max?: number }): number | null {
  if (value === null) {
    return null;
  }
  let result = value;
  if (typeof limits?.min === 'number' && result < limits.min) {
    result = limits.min;
  }
  if (typeof limits?.max === 'number' && result > limits.max) {
    result = limits.max;
  }
  return result;
}

function coerceDate(value: QueryRecordValue | string | null | undefined): string | null {
  if (Array.isArray(value)) {
    const [first] = value;
    return coerceDate(first);
  }
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const timestamp = Date.parse(trimmed);
  if (Number.isNaN(timestamp)) {
    return null;
  }
  return new Date(timestamp).toISOString().slice(0, 10);
}

function toStringArray(value: QueryRecordValue): string[] {
  if (Array.isArray(value)) {
    return value
      .flatMap((entry) => entry.split(','))
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
  }
  return [];
}

function isAssetType(value: string): value is PropertyAssetType {
  return PROPERTY_TYPE_SET.has(value as PropertyAssetType);
}

function isReadinessStatus(value: string): value is UnitReadinessStatus {
  return READINESS_VALUES.includes(value as UnitReadinessStatus);
}

function appendRange(
  params: URLSearchParams,
  range: RangeFilterValue,
  minKey: string,
  maxKey: string,
): void {
  if (typeof range.min === 'number') {
    params.set(minKey, range.min.toString());
  }
  if (typeof range.max === 'number') {
    params.set(maxKey, range.max.toString());
  }
}

function normalizeQueryRecord(
  input: URLSearchParams | PublicPropertyFilterQueryParams | QueryRecord,
): QueryRecord {
  if (input instanceof URLSearchParams) {
    const record: QueryRecord = {};
    input.forEach((value, key) => {
      const existing = record[key];
      if (existing === undefined) {
        record[key] = value;
      } else if (Array.isArray(existing)) {
        existing.push(value);
      } else {
        record[key] = [existing, value];
      }
    });
    return record;
  }
  return { ...input };
}
